import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Phone, MapPin, Building2, Calendar, Globe, KeyRound, Copy, Trash2, UserCheck, UserX, Briefcase, Edit } from 'lucide-react';
import AdminProfileEditor from '@/components/AdminProfileEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProviderDetail = () => {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePasscode, setDeletePasscode] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const fetchProvider = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const res = await axios.get(`${API}/admin/service-providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const providers = res.data.providers || res.data;
      const found = providers.find(p => p.id === providerId);
      if (found) {
        setProvider(found);
        setNewEmail(found.email || '');
      } else {
        toast.error('Provider not found');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      toast.error('Failed to load provider');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProvider(); }, [providerId]);

  const handleToggleStatus = async () => {
    const newStatus = provider.status === 'active' ? 'inactive' : 'active';
    setActionLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      await axios.patch(`${API}/admin/service-providers/${providerId}/status`, {
        status: newStatus
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Provider ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchProvider();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setActionLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      const res = await axios.post(`${API}/admin/service-providers/${providerId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResetPasswordData({ newPassword: res.data.new_password, name: provider.full_name });
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!newEmail.trim()) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.patch(`${API}/admin/service-providers/${providerId}/email`, {
        email: newEmail
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Email updated');
      setEditingEmail(false);
      fetchProvider();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API}/admin/service-providers/${providerId}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { passcode: deletePasscode }
      });
      toast.success('Provider deleted');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
    return phone;
  };

  if (loading) return <div className="p-8 text-center text-stone-500">Loading...</div>;
  if (!provider) return null;

  return (
    <div className="min-h-screen bg-[#faf9f6] p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="mb-4 text-stone-600 hover:text-[#1A4D2E]">
          <ArrowLeft className="mr-2" size={20} />
          Back to Dashboard
        </Button>

        <h1 className="text-2xl sm:text-3xl font-bold text-[#1A4D2E] mb-6" style={{fontFamily: '"Playfair Display", serif'}}>
          Provider Details
        </h1>

        {/* Provider Info */}
        <Card className="p-4 sm:p-8 bg-white rounded-2xl mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-stone-900">{provider.full_name}</h2>
              <p className="text-stone-500">ID: {provider.id}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
              provider.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-stone-200 text-stone-600'
            }`}>
              {provider.status === 'active' ? 'Active' : 'Inactive'}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Briefcase size={18} className="text-[#1A4D2E] mt-0.5 shrink-0" />
              <div>
                <Label className="text-stone-500 text-xs">Service Type</Label>
                <p className="text-stone-900 font-medium capitalize">{provider.service_type}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail size={18} className="text-[#1A4D2E] mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <Label className="text-stone-500 text-xs">Email</Label>
                {editingEmail ? (
                  <div className="flex gap-2">
                    <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="h-8 text-sm" data-testid="edit-email-input" />
                    <Button size="sm" onClick={handleUpdateEmail} className="bg-[#1A4D2E] text-white h-8">Save</Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditingEmail(false); setNewEmail(provider.email); }} className="h-8">Cancel</Button>
                  </div>
                ) : (
                  <p className="text-stone-900 break-all">{provider.email}</p>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone size={18} className="text-[#1A4D2E] mt-0.5 shrink-0" />
              <div>
                <Label className="text-stone-500 text-xs">Phone</Label>
                <p className="text-stone-900">{formatPhone(provider.phone)}</p>
              </div>
            </div>
            {provider.company_name && (
              <div className="flex items-start gap-3">
                <Building2 size={18} className="text-[#1A4D2E] mt-0.5 shrink-0" />
                <div>
                  <Label className="text-stone-500 text-xs">Company</Label>
                  <p className="text-stone-900">{provider.company_name}</p>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <MapPin size={18} className="text-[#1A4D2E] mt-0.5 shrink-0" />
              <div>
                <Label className="text-stone-500 text-xs">Location</Label>
                <p className="text-stone-900">{provider.city}, {provider.state} {provider.zip_code}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar size={18} className="text-[#1A4D2E] mt-0.5 shrink-0" />
              <div>
                <Label className="text-stone-500 text-xs">Registered</Label>
                <p className="text-stone-900">{new Date(provider.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            {provider.website && (
              <div className="flex items-start gap-3">
                <Globe size={18} className="text-[#1A4D2E] mt-0.5 shrink-0" />
                <div>
                  <Label className="text-stone-500 text-xs">Website</Label>
                  <a href={provider.website} target="_blank" rel="noopener noreferrer" className="text-[#1A4D2E] hover:underline">{provider.website}</a>
                </div>
              </div>
            )}
            {provider.agent_name && (
              <div className="flex items-start gap-3">
                <UserCheck size={18} className="text-[#1A4D2E] mt-0.5 shrink-0" />
                <div>
                  <Label className="text-stone-500 text-xs">Agent</Label>
                  <p className="text-stone-900">{provider.agent_name}</p>
                </div>
              </div>
            )}
          </div>

          {provider.service_area && (
            <div className="mt-4">
              <Label className="text-stone-500 text-xs">Service Area</Label>
              <p className="text-stone-900">{provider.service_area}</p>
            </div>
          )}

          {provider.description && (
            <div className="mt-4">
              <Label className="text-stone-500 text-xs">Description</Label>
              <p className="text-stone-900 bg-stone-50 p-3 rounded-lg mt-1">{provider.description}</p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 pt-6 border-t border-stone-200 space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-stone-700">Status</Label>
              <div className="flex items-center gap-2">
                <Switch
                  checked={provider.status === 'active'}
                  onCheckedChange={handleToggleStatus}
                  disabled={actionLoading}
                  className="data-[state=checked]:bg-[#1A4D2E]"
                  data-testid="provider-status-toggle"
                />
                <span className="text-sm text-stone-600">{provider.status === 'active' ? 'Active' : 'Inactive'}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleResetPassword}
                disabled={actionLoading}
                variant="outline"
                className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
                data-testid="reset-provider-pwd-btn"
              >
                <KeyRound size={18} className="mr-2" />
                Reset Password
              </Button>

              <Button
                onClick={() => setEditingEmail(true)}
                variant="outline"
                className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
                data-testid="edit-provider-email-btn"
              >
                <Mail size={18} className="mr-2" />
                Edit Email
              </Button>

              <Button
                onClick={() => setShowProfileEditor(true)}
                variant="outline"
                className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E]/10"
                data-testid="edit-public-profile-btn"
              >
                <Edit size={18} className="mr-2" />
                Edit Public Profile
              </Button>

              <Button
                onClick={() => setShowDeleteDialog(true)}
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                data-testid="delete-provider-btn"
              >
                <Trash2 size={18} className="mr-2" />
                Delete Provider
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={!!resetPasswordData} onOpenChange={() => setResetPasswordData(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1A4D2E]">Password Reset Successful</DialogTitle>
            <DialogDescription className="sr-only">New password details</DialogDescription>
          </DialogHeader>
          {resetPasswordData && (
            <div className="space-y-4 mt-4">
              <p className="text-stone-600">New password for <strong>{resetPasswordData.name}</strong>:</p>
              <div className="flex items-center gap-2 bg-stone-50 p-3 rounded-lg border">
                <code className="text-lg font-mono flex-1">{resetPasswordData.newPassword}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(resetPasswordData.newPassword); toast.success('Copied!'); }}>
                  <Copy size={16} />
                </Button>
              </div>
              <p className="text-sm text-red-600">Please save this password. It won't be shown again.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600">Delete Provider</DialogTitle>
            <DialogDescription>
              This will permanently delete {provider?.full_name}. Enter admin passcode to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <input
              type="password"
              placeholder="Enter admin passcode"
              value={deletePasscode}
              onChange={(e) => setDeletePasscode(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg bg-white text-sm"
              data-testid="delete-passcode-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeletePasscode(''); }} className="flex-1">Cancel</Button>
              <Button onClick={handleDelete} disabled={deleteLoading || !deletePasscode} variant="destructive" className="flex-1" data-testid="confirm-delete-btn">
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AdminProfileEditor
        open={showProfileEditor}
        onClose={(saved) => { setShowProfileEditor(false); if (saved) fetchProvider(); }}
        userId={providerId}
        userType="provider"
      />
    </div>
  );
};

export default AdminProviderDetail;
